import ConfirmDialog from "@components/ConfirmDialog";
import { CustomCustomerListItem } from "@components/denseForm/customListItem/customCustomer";
import MenuAutocompleteV2 from "@components/denseForm/MenuAutocompleteV2";
import { RHFTextField } from "@components/hook-form";
import { Option } from "@components/hook-form/RHFAutocomplete";
import Label from "@components/Label";
import {
  Box,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import useCustomer from "@pages/ManualDocumentV2/hooks/useCustomer";
import { getManualDocumentCountryValue } from "@pages/ManualDocumentV2/utils/country";
import { createVendorCompanyIdOptions } from "@pages/ManualDocumentV2/utils/createVendorCompanyIdOptions";
import { Pencil, Trash } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import {
  resolveSectionOverrides,
  sortSectionFields,
} from "../../core/sections";
import type { IFormEngine } from "../../core/types";
import { useCommonStyles } from "../../hooks/useCommonStyles";
import { EditCustomerInformation } from "./EditCustomerInformation";

interface ICustomerInformationProps {
  translate: (text: any, options?: any) => string;
  engine: IFormEngine;
}

const ALL_CUSTOMER_FIELDS = [
  "customerCompanyID",
  "customerEmail",
  "customerCompanyAddress",
  "currency",
  "customerCompanyStreet",
  "customerCompanyStreetNumber",
  "customerCompanyLocality",
  "customerCompanyZipCode",
  "customerCompanyRegion",
  "customerCountry",
];

export const CustomerInformation = ({
  translate,
  engine,
}: ICustomerInformationProps) => {
  const {
    watch,
    setValue,
    clearErrors,
    formState: { errors },
  } = useFormContext();
  const { inputContainer } = useCommonStyles();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [hasOpenedEdit, setHasOpenedEdit] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [tooltipEnabled, setTooltipEnabled] = useState(false);
  const [customerCompanyIDOptions, setCustomerCompanyIDOptions] = useState<
    Option[]
  >([]);
  const { isFormReset } = engine;
  const isAccountPayable = engine.config.accountClass === "AccountPayable";

  const isPO = engine.config.documentClass === "PurchaseOrder";
  const isFrance = engine.config.complianceStandardKey === "FrancePA";

  const { hiddenFields, extraFields, missingChips, hidden } = useMemo(() => {
    const ov = resolveSectionOverrides(engine.config, "customer");
    return {
      hiddenFields: ov.hiddenFields ?? [],
      extraFields: sortSectionFields(ov.extraFields ?? []),
      missingChips: ov.missingChips ?? [],
      hidden: new Set(ov.hiddenFields ?? []),
    };
  }, [engine.config]);
  const isVisible = (key: string) => !hidden.has(key);

  const watched = watch();

  const {
    customerCompanyID,
    customerCompanyName,
    _customerCompanyIDSource: customerCompanyIDSource,
    customerCompanyAddress,
    customerEmail,
    logoURL,
    currency,
    customerCompanyStreet,
    customerCompanyStreetNumber,
    customerCompanyLocality,
    customerCompanyZipCode,
    customerCompanyRegion,
    customerCountry,
    customerPeppolSchemeId,
    customerPeppolParticipantId,
  } = watched;

  useEffect(() => {
    createVendorCompanyIdOptions(
      customerCompanyIDSource ?? "",
      setCustomerCompanyIDOptions,
    );
  }, [customerCompanyIDSource]);

  useEffect(() => {
    if (isEditOpen) setHasOpenedEdit(true);
  }, [isEditOpen]);

  const emptyMissingChips = missingChips.filter((c) => {
    const v = watched[c.name];
    return v === "" || v === undefined || v === null;
  });

  const optionalAddressKeys = [
    "customerCompanyStreet",
    "customerCompanyStreetNumber",
    "customerCompanyLocality",
    "customerCompanyZipCode",
    "customerCompanyRegion",
  ] as const;

  const optionalValues = {
    customerCompanyStreet,
    customerCompanyStreetNumber,
    customerCompanyLocality,
    customerCompanyZipCode,
    customerCompanyRegion,
  } as Record<string, any>;

  const hasCustomerInfo = !!customerCompanyName;

  const CUSTOMER_ERROR_FIELDS = [
    "customerCompanyAddress",
    "customerEmail",
    "customerCountry",
    !isAccountPayable ? "currency" : "",
  ].filter(Boolean) as string[];

  const errorChips = Object.entries(errors ?? {})
    ?.filter(([key]) => CUSTOMER_ERROR_FIELDS.includes(key) && isVisible(key))
    .map(([field, error]) => ({
      field,
      message: (error as any)?.message || translate(field),
    }));

  const { selectOption, setSelectOption, ...customerOptionProps } = useCustomer(
    {
      label: customerCompanyName,
      value: customerCompanyName,
    },
  );

  const resetCustomerFields = () => {
    setSelectOption({ label: "", value: "" });
    setValue("customerCompanyName", "");
    ALL_CUSTOMER_FIELDS.forEach((field) => setValue(field, ""));
    if (isFrance) {
      setValue("customerPeppolSchemeId", "");
      setValue("customerPeppolParticipantId", "");
      setValue("_customerPeppolSelector", null);
      setValue("customerId", "");
    }
    clearErrors(["customerCompanyName", ...ALL_CUSTOMER_FIELDS]);
    setHasOpenedEdit(false);
  };

  const handleConfirmDelete = () => {
    resetCustomerFields();
    setIsConfirmOpen(false);
  };

  useEffect(() => {
    if (isFormReset > 0 && !isPO) {
      resetCustomerFields();
    }
  }, [isFormReset]);

  if (!hasCustomerInfo && !isEditOpen && !hasOpenedEdit) {
    return (
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Typography variant="h6">
            {translate("customer_information")}{" "}
            {isPO && (
              <Typography
                variant="h6"
                component="span"
                sx={{ fontWeight: 500 }}
              >
                {translate("your_information")}
              </Typography>
            )}
          </Typography>
        </Grid>
        {isAccountPayable ? (
          <Grid item xs={12}>
            <RHFTextField
              name="customerCompanyName"
              label={translate("companyName")}
              fullWidth
            />
          </Grid>
        ) : (
          <Grid item xs={12}>
            <MenuAutocompleteV2
              optionTitle={translate("Customer")}
              getOptionLabel={(option: any) => option?.label ?? ""}
              error={errors.customerCompanyName?.message as string | undefined}
              onInputChange={(event) => {
                setSelectOption({
                  label: event?.target.value,
                  value: event?.target.value,
                });
                setValue("customerCompanyName", event?.target.value ?? "", {
                  shouldDirty: true,
                });
                clearErrors("customerCompanyName");
              }}
              onChange={(event, newValue: any) => {
                const {
                  name,
                  registrationNumber,
                  email,
                  street,
                  streetNumber,
                  locality,
                  zipCode,
                  address,
                  currency,
                  region,
                  country,
                  customerId,
                  id,
                } = newValue;
                setSelectOption({
                  label: newValue?.name,
                  value: newValue?.name,
                });

                const next: Record<string, any> = {
                  customerCompanyName: name ?? "",
                  customerCompanyID: registrationNumber ?? "",
                  customerId: customerId ?? id ?? "",
                  customerEmail: email ?? "",
                  customerCompanyAddress: address ?? "",
                  currency: currency ?? "",
                  customerCompanyStreet: street ?? "",
                  customerCompanyStreetNumber: streetNumber ?? "",
                  customerCompanyLocality: locality ?? "",
                  customerCompanyZipCode: zipCode ?? "",
                  customerCompanyRegion: region ?? "",
                  customerCountry: getManualDocumentCountryValue(country),
                };

                const fieldsToClear: string[] = [];
                Object.entries(next).forEach(([key, value]) => {
                  setValue(key, value, {
                    shouldValidate: false,
                    shouldDirty: true,
                  });
                  if (value !== "" && value !== null && value !== undefined) {
                    fieldsToClear.push(key);
                  }
                });
                if (fieldsToClear.length) clearErrors(fieldsToClear as any);
              }}
              renderOption={(props, option) => {
                return <CustomCustomerListItem {...props} option={option} />;
              }}
              renderOptionHeader={() => {
                return <> </>;
              }}
              {...customerOptionProps}
              value={selectOption}
              onSearch={customerOptionProps.getAsyncOptions}
              enableSearch={true}
              label={translate("companyName")}
            />
          </Grid>
        )}
      </Grid>
    );
  }

  const visibleOptionalAddress = optionalAddressKeys.filter(isVisible);
  const showMissingCompanyNameChip = !customerCompanyName;
  const showMissingIDChip =
    isVisible("customerCompanyID") &&
    !customerCompanyID &&
    !errorChips.some((e) => e.field === "customerCompanyID");
  const showMissingCountryChip =
    isVisible("customerCountry") &&
    !customerCountry &&
    !errorChips.some((e) => e.field === "customerCountry");
  const showMissingCurrencyChip =
    !isAccountPayable &&
    isVisible("currency") &&
    !currency &&
    !errorChips.some((e) => e.field === "currency");

  const showMissingAddressChips = visibleOptionalAddress.some(
    (k) =>
      optionalValues[k] === "" ||
      optionalValues[k] === undefined ||
      optionalValues[k] === null,
  );

  const handleShowTooltip = ({ currentTarget }: React.MouseEvent<Element>) => {
    if (currentTarget.scrollWidth > currentTarget.clientWidth) {
      setTooltipEnabled(true);
    }
  };
  const hideTooltip = () => setTooltipEnabled(false);

  return (
    <>
      <ConfirmDialog
        open={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        title={translate("delete_customer_title")}
        content={translate("delete_customer_warning")}
        onConfirm={handleConfirmDelete}
        confirmLoading={false}
        isManualFormComponent={true}
        confirmText="remove"
      />
      <Grid container spacing={1}>
        {isEditOpen && (
          <EditCustomerInformation
            open={isEditOpen}
            onClose={() => setIsEditOpen(false)}
            translate={translate}
            engine={engine}
            hiddenFields={hiddenFields}
            extraFields={extraFields}
            customerCompanyIDOptions={customerCompanyIDOptions}
          />
        )}
        <Grid item xs={12}>
          <Typography variant="h6">
            {translate("customer_information")}{" "}
            {isPO && (
              <Typography
                variant="h6"
                component="span"
                sx={{ fontWeight: 500 }}
              >
                {translate("your_information")}
              </Typography>
            )}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Box sx={inputContainer}>
            <Grid container alignItems="center">
              <Grid item xs>
                <Stack spacing={1}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Tooltip
                      title={customerCompanyName}
                      onMouseEnter={handleShowTooltip}
                      onMouseLeave={hideTooltip}
                      disableHoverListener={!tooltipEnabled}
                      placement="top"
                    >
                      <Typography
                        variant="subtitle1"
                        fontWeight={600}
                        noWrap
                        sx={{
                          fontSize: "12px",
                          textOverflow: "ellipsis",
                          maxWidth: 195,
                          cursor: "pointer",
                        }}
                      >
                        {customerCompanyName}
                      </Typography>
                    </Tooltip>

                    {(errorChips.length > 0 ||
                      showMissingCompanyNameChip ||
                      showMissingIDChip ||
                      showMissingCountryChip ||
                      showMissingCurrencyChip ||
                      (isAccountPayable && !logoURL) ||
                      showMissingAddressChips ||
                      emptyMissingChips.length > 0) && (
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 0.75,
                          rowGap: 0.5,
                          alignItems: "center",
                        }}
                      >
                        {showMissingCompanyNameChip && (
                          <Label variant="ghost" color="error">
                            {translate("please_enter_company_name")}
                          </Label>
                        )}
                        {showMissingIDChip && (
                          <Label variant="ghost" color="error">
                            {translate("please_select_company_id")}
                          </Label>
                        )}
                        {showMissingCountryChip && (
                          <Label variant="ghost" color="error">
                            {translate("please_select_country")}
                          </Label>
                        )}
                        {showMissingCurrencyChip && (
                          <Label variant="ghost" color="error">
                            {translate("please_select_currency")}
                          </Label>
                        )}
                        {errorChips.map((error) => (
                          <Label
                            key={error.field}
                            variant="ghost"
                            color="error"
                          >
                            {translate(error.message)}
                          </Label>
                        ))}
                      </Box>
                    )}
                  </Box>

                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: "center" }}
                  >
                    {isVisible("customerCompanyID") && customerCompanyID && (
                      <Tooltip
                        title={customerCompanyID}
                        onMouseEnter={handleShowTooltip}
                        onMouseLeave={hideTooltip}
                        disableHoverListener={!tooltipEnabled}
                        placement="top"
                      >
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{
                            fontSize: "12px",
                            textOverflow: "ellipsis",
                            maxWidth: 195,
                            cursor: "pointer",
                          }}
                        >
                          {customerCompanyID}
                        </Typography>
                      </Tooltip>
                    )}
                    {isVisible("customerEmail") && customerEmail && (
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{
                          fontSize: "12px",
                          textOverflow: "ellipsis",
                          maxWidth: 195,
                          cursor: "pointer",
                        }}
                      >
                        • {customerEmail}
                      </Typography>
                    )}
                    {isVisible("customerCountry") && customerCountry && (
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{
                          fontSize: "12px",
                          textOverflow: "ellipsis",
                          maxWidth: 195,
                          cursor: "pointer",
                        }}
                      >
                        • {customerCountry}
                      </Typography>
                    )}
                    {isVisible("currency") && currency && (
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{
                          fontSize: "12px",
                          textOverflow: "ellipsis",
                          maxWidth: 195,
                          cursor: "pointer",
                        }}
                      >
                        • {currency}
                      </Typography>
                    )}
                  </Stack>

                  {isVisible("customerCompanyAddress") &&
                    customerCompanyAddress && (
                      <Tooltip
                        title={customerCompanyAddress}
                        onMouseEnter={handleShowTooltip}
                        onMouseLeave={hideTooltip}
                        disableHoverListener={!tooltipEnabled}
                        placement="top"
                      >
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{
                            fontSize: "12px",
                            textOverflow: "ellipsis",
                            maxWidth: 300,
                            cursor: "pointer",
                          }}
                        >
                          {customerCompanyAddress}
                        </Typography>
                      </Tooltip>
                    )}

                  <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    sx={{ alignItems: "center" }}
                  >
                    {isVisible("customerCompanyStreet") &&
                      customerCompanyStreet && (
                        <Tooltip
                          title={customerCompanyStreet}
                          onMouseEnter={handleShowTooltip}
                          onMouseLeave={hideTooltip}
                          disableHoverListener={!tooltipEnabled}
                          placement="top"
                        >
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{
                              fontSize: "12px",
                              textOverflow: "ellipsis",
                              maxWidth: 150,
                              cursor: "pointer",
                            }}
                          >
                            {customerCompanyStreet}
                          </Typography>
                        </Tooltip>
                      )}
                    {isVisible("customerCompanyStreetNumber") &&
                      customerCompanyStreetNumber && (
                        <Tooltip
                          title={customerCompanyStreetNumber}
                          onMouseEnter={handleShowTooltip}
                          onMouseLeave={hideTooltip}
                          disableHoverListener={!tooltipEnabled}
                          placement="top"
                        >
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{
                              fontSize: "12px",
                              textOverflow: "ellipsis",
                              maxWidth: 100,
                              cursor: "pointer",
                            }}
                          >
                            • {customerCompanyStreetNumber}
                          </Typography>
                        </Tooltip>
                      )}
                    {isVisible("customerCompanyLocality") &&
                      customerCompanyLocality && (
                        <Tooltip
                          title={customerCompanyLocality}
                          onMouseEnter={handleShowTooltip}
                          onMouseLeave={hideTooltip}
                          disableHoverListener={!tooltipEnabled}
                          placement="top"
                        >
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{
                              fontSize: "12px",
                              textOverflow: "ellipsis",
                              maxWidth: 150,
                              cursor: "pointer",
                            }}
                          >
                            • {customerCompanyLocality}
                          </Typography>
                        </Tooltip>
                      )}
                    {isVisible("customerCompanyRegion") &&
                      customerCompanyRegion && (
                        <Tooltip
                          title={customerCompanyStreet}
                          onMouseEnter={handleShowTooltip}
                          onMouseLeave={hideTooltip}
                          disableHoverListener={!tooltipEnabled}
                          placement="top"
                        >
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{
                              fontSize: "12px",
                              textOverflow: "ellipsis",
                              maxWidth: 100,
                              cursor: "pointer",
                            }}
                          >
                            • {customerCompanyRegion}
                          </Typography>
                        </Tooltip>
                      )}
                    {isVisible("customerCompanyZipCode") &&
                      customerCompanyZipCode && (
                        <Tooltip
                          title={customerCompanyZipCode}
                          onMouseEnter={handleShowTooltip}
                          onMouseLeave={hideTooltip}
                          disableHoverListener={!tooltipEnabled}
                          placement="top"
                        >
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{
                              fontSize: "12px",
                              textOverflow: "ellipsis",
                              maxWidth: 100,
                              cursor: "pointer",
                            }}
                          >
                            • {customerCompanyZipCode}
                          </Typography>
                        </Tooltip>
                      )}
                  </Stack>

                  {isFrance &&
                    (customerPeppolSchemeId || customerPeppolParticipantId) && (
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{ alignItems: "center", mt: 0.5 }}
                      >
                        {customerPeppolSchemeId && (
                          <Typography variant="body2" sx={{ fontSize: "12px" }}>
                            Scheme ID ({customerPeppolSchemeId})
                          </Typography>
                        )}
                        {customerPeppolParticipantId && (
                          <Typography variant="body2" sx={{ fontSize: "12px" }}>
                            {customerPeppolSchemeId ? " • " : ""}
                            Peppol Participant ID ({customerPeppolParticipantId}
                            )
                          </Typography>
                        )}
                      </Stack>
                    )}
                </Stack>
              </Grid>
              <Grid item>
                <IconButton onClick={() => setIsEditOpen(true)}>
                  <Pencil strokeWidth="1.5px" size="18px" />
                </IconButton>
                {!isAccountPayable && (
                  <IconButton onClick={() => setIsConfirmOpen(true)}>
                    <Trash strokeWidth="1.5px" size="18px" />
                  </IconButton>
                )}
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </>
  );
};
